<template>
  <div v-on-clickaway="onBlur" class="quicksearch">
    <a-text-input
      v-model="searchPhrase"
      ref="search"
      search-icon
      :placeholder="settings.placeholder"
      :max-length="$options.consts.QUICK_SEARCH_MAX_LENGTH_LIMITATION"
      @input="onSearch"
      @focus="onFocus"
      @search="redirectToSearchPage"
      @keyup.enter="redirectToSearchPage"
    />
    <quick-search-results
      v-if="isDropdownVisible"
      :items="items"
      :settings="settings"
      :in-progress="inProgress"
      :hide-result-button="hideResultButton"
      @blur="onBlur"
      @hook:mounted="isFocused = true"
    />
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue-clickaway'
import debounce from 'debounce'

import { mapActionsLazy } from '@/plugins/helper'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import mixins from '@/utils/mixins'
import { ROUTE_NAME } from 'enums/routes'
import QuickSearchResults from 'shared/AQuickSearch/QuickSearchResults'
import { SEARCH_TRACKING_EVENT_TYPE } from 'enums/track-search'
import { MIN_CHAR_COUNT_FOR_TRACKING_SEARCH } from '@/utils/mixins/track-search'
import { QUICK_SEARCH_MAX_LENGTH_LIMITATION } from 'enums/quicksearch'

export const SEARCH_MIN_CHAR_COUNT = 2

export const QUICKSEARCH_TYPE = {
  TERMS: 'TERMS',
  TAGS: 'TAGS',
  FM_DIR_SEARCH_HISTORY: 'FM_DIR_SEARCH_HISTORY'
}

const DEFAULT_SEARCH_HISTORY_PAGE = 0
const DEFAULT_SEARCH_HISTORY_TAKE = 10

export default {
  name: 'AQuickSearch',
  components: { QuickSearchResults },
  mixins: [mixins.urlGenerators, mixins.trackSearch],
  directives: { onClickaway },
  props: {
    type: propValidator([PROP_TYPES.STRING], true, null, value =>
      Object.values(QUICKSEARCH_TYPE).includes(value)
    ),
    hideResultButton: propValidator([PROP_TYPES.BOOLEAN], false, false),
    useTrackSearch: propValidator([PROP_TYPES.BOOLEAN], false, true)
  },
  consts: {
    searchTrackingEventByType: {
      [QUICKSEARCH_TYPE.TERMS]:
        SEARCH_TRACKING_EVENT_TYPE.FM_TERMS_QUICK_SEARCH,
      [QUICKSEARCH_TYPE.TAGS]: SEARCH_TRACKING_EVENT_TYPE.FM_TAGS_QUICK_SEARCH,
      [QUICKSEARCH_TYPE.FM_DIR_SEARCH_HISTORY]:
        SEARCH_TRACKING_EVENT_TYPE.FMDIR_SEARCH_HISTORY_QUICK_SEARCH
    },
    QUICK_SEARCH_MAX_LENGTH_LIMITATION,
    resetComponentDataFields: [
      'items',
      'inProgress',
      'isFocused',
      'searchPhrase'
    ]
  },
  data() {
    return {
      items: [],
      inProgress: false,
      isFocused: false,
      searchPhrase: ''
    }
  },
  computed: {
    currentTrackingEventType() {
      return this.$options.consts.searchTrackingEventByType[this.type]
    },
    isDropdownVisible() {
      return (
        (this.isFocused || this.$refs?.search?.isFocused) &&
        this.searchPhrase.length >= SEARCH_MIN_CHAR_COUNT &&
        this.$helper.isTrimmedStringNotEmpty(this.searchPhrase)
      )
    },
    settings() {
      const settings = {
        [QUICKSEARCH_TYPE.TERMS]: {
          handler: this.requestTermsQuicksearch,
          handlerParams: this.termsRequestParams,
          redirectRouteName: ROUTE_NAME.TERMS_SEARCH_RESULTS,
          viewAllResultsClickHandler: this.redirectToSearchPage,
          generateItemUrl: this.generateTermRoute,
          placeholder: 'Search for a term'
        },
        [QUICKSEARCH_TYPE.TAGS]: {
          handler: this.requestTagsQuicksearch,
          handlerParams: this.tagsRequestParams,
          redirectRouteName: ROUTE_NAME.TAG_SEARCH_RESULTS,
          viewAllResultsClickHandler: this.redirectToSearchPage,
          generateItemUrl: this.generateTagPath,
          placeholder: 'Search for a tag'
        },
        [QUICKSEARCH_TYPE.FM_DIR_SEARCH_HISTORY]: {
          handler: this.requestFmDirSearchHistoryQuicksearch,
          handlerParams: this.searchHistoryRequestParams,
          viewAllResultsClickHandler: this.redirectToSearchPage,
          generateItemUrl: this.generateFmDirSearchResultUrl,
          placeholder: 'Search my saved history'
        }
      }

      return settings[this.type]
    },
    searchHistoryRequestParams() {
      return {
        queryParams: {
          page: DEFAULT_SEARCH_HISTORY_PAGE,
          take: DEFAULT_SEARCH_HISTORY_TAKE,
          searchPhrase: this.searchPhrase
        }
      }
    },
    termsRequestParams() {
      return {
        queryParams: {
          searchPhrase: this.searchPhrase
        }
      }
    },
    tagsRequestParams() {
      return {
        queryParams: {
          searchPhrase: this.searchPhrase
        }
      }
    }
  },
  watch: {
    '$route.path': {
      handler() {
        this.clearSearchPhrase()
        this.onBlur()
      }
    },
    searchPhrase: function() {
      if (
        this.useTrackSearch &&
        this.searchPhrase?.length >= MIN_CHAR_COUNT_FOR_TRACKING_SEARCH
      ) {
        this.$_trackSearch_debouncedTrackSearch(
          this.searchPhrase,
          this.currentTrackingEventType
        )
      }
    }
  },
  methods: {
    ...mapActionsLazy({
      requestTermsQuicksearch: 'globalSearch/requestTermsQuicksearch',
      requestTagsQuicksearch: 'globalSearch/requestTagsQuicksearch',
      requestFmDirSearchHistoryQuicksearch:
        'personal-area/requestFmDirSearchHistoryQuicksearch'
    }),
    onBlur() {
      this.isFocused = false
    },
    onFocus() {
      this.isFocused = true
      this.requestQuicksearch(this.searchPhrase)
    },
    onSearch(searchPhrase) {
      this.inProgress = true
      this.debouncedSearch(searchPhrase)
    },
    debouncedSearch: debounce(function(searchPhrase) {
      this.requestQuicksearch(searchPhrase)
    }, 200),
    async requestQuicksearch(searchPhrase) {
      if (searchPhrase.length < SEARCH_MIN_CHAR_COUNT) return

      try {
        const params = this.settings.handlerParams || { searchPhrase }
        const { Items } = await this.settings.handler(params)
        this.items = Items
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    redirectToSearchPage() {
      if (this.searchPhrase.length < SEARCH_MIN_CHAR_COUNT) return

      this.$router.push({
        name: this.settings.redirectRouteName,
        query: {
          searchPhrase: this.searchPhrase
        }
      })
      this.$helper.resetComponentData(
        this,
        this.$options.consts.resetComponentDataFields
      )
      this.$helper.blurActiveElement()
    },
    clearSearchPhrase() {
      this.searchPhrase = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.quicksearch {
  position: relative;

  .quicksearch__dropdown {
    position: absolute;
    top: 38px;
    left: 0;
    width: 100%;
    min-height: 45px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid $c--main;
    box-shadow: 0 10px 10px 0 rgba($c--black, 0.3);
    background: $c--white;
    z-index: 1;
  }

  .quicksearch__results {
    width: 100%;
  }

  .quicksearch__list {
    padding: 12px 30px 20px 20px;
  }

  .quicksearch__list-item {
    line-height: 34px;
    border-bottom: solid 2px $c--row-delimiter;

    & > * {
      color: $c--main;
      display: inline-block;
      width: 100%;
    }

    &:last-child {
      border: none;
    }
  }

  .quicksearch__btn {
    width: 100%;
  }

  .quicksearch__no-results {
    text-align: center;
    color: $c--main;
  }
}
</style>
